import {
  SegmentActionName,
  SegmentObjectName,
  track,
  TrackingEventName,
  trackLegacyEvent,
} from '@heidi/analytics/events';
import { getCurrentSessionId } from '@heidi/runtime/shell';
import { type VoiceStyle } from '@heidi/schema/ml-scribe';

export function chooseDictation(sessionId: string) {
  // NOTE: This fires whenever the session's transcribe mode is set to
  // DICTATE — including automatic paths (HealthDirect default, upload
  // audio dialog), not just user-initiated dropdown clicks. It must NOT
  // dispatch `transcription_started`; that event only fires once
  // recording actually begins (see `sessionStartRecording`).
  // The explicit "user picked Dictate in the start dropdown" signal is
  // tracked as a `button_clicked` event from `StartButtonDropdown`.
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionDictation,
      sessionId: sessionId,
    },
  });
}

export function chooseTranscribe() {
  // See `chooseDictation` above for why `transcription_started` is not
  // dispatched here.
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionTranscribe,
    },
  });
}

export function sessionChooseMicInput(microphone: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionChooseMicInput,
      microphone: microphone,
    },
  });

  // V2 — Amplitude + Datadog only
  const sessionId = getCurrentSessionId();
  if (sessionId) {
    track({
      options: { amplitude: false },
      event_name: TrackingEventName.SessionUpdated,
      props: {
        session_id: sessionId,
        update_action: 'mic_input_changed',
        microphone,
      },
    });
  }
}

/** @deprecated use trackSessionCreated instead */
export function sessionCreated(sessionId: string) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionCreate,
      sessionId: sessionId,
    },
  });
}

// Extracted from src/lib/analytics/scribe/session.ts; history: git log --follow src/lib/analytics/scribe/session.ts
export function sessionTemplateUpdated(input: { sessionId: string; templateId: string }) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionTemplateUpdated,
      templateId: input.templateId,
      sessionId: input.sessionId,
    },
  });
}

export function sessionVoiceUpdated(input: {
  voiceStyle?: VoiceStyle;
  oldVoiceStyle?: VoiceStyle;
  sessionId: string;
}) {
  trackLegacyEvent({
    name: SegmentObjectName.Session,
    props: {
      action: SegmentActionName.SessionVoiceUpdated,
      voiceStyle: input.voiceStyle,
      oldVoiceStyle: input.oldVoiceStyle,
      sessionId: input.sessionId,
    },
  });
}
